import { canvasSize, type Frame, framePlacement } from "./settings.ts";
import { drawWatermark } from "./watermark.ts";

/**
 * Turning a run of frames into a file the browser can hand over.
 *
 * `MediaRecorder` over a canvas capture stream, not WebCodecs. `VideoEncoder`
 * would encode faster than real time and give exact control of frame timing,
 * but it emits raw encoded chunks — turning those into a file anybody can
 * open needs a muxer, which is a dependency this app does not have and which
 * would be carried by every visitor who never exports anything. MediaRecorder
 * produces a finished container with nothing added, at the cost of running
 * in real time: a thirty-second film takes thirty seconds to record, and the
 * screen says so rather than pretending otherwise.
 *
 * Nothing leaves the browser. The frames are already coming down for the
 * scrubber; asking a server to assemble what is already here would be a
 * round trip for a file the reader is standing next to.
 */

/** The containers worth asking for, best first. */
const CANDIDATE_TYPES = [
  // Safari 17+ records straight to mp4, which is the one file everything
  // opens. Asked for first for exactly that reason.
  "video/mp4;codecs=avc1.42E01E",
  "video/mp4",
  "video/webm;codecs=vp9",
  "video/webm;codecs=vp8",
  "video/webm",
] as const;

/** What this browser can actually write, or null if it cannot record at all. */
export function supportedMimeType(
  isSupported: (type: string) => boolean = (type) =>
    typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(type),
): string | null {
  if (typeof MediaRecorder === "undefined") return null;
  return CANDIDATE_TYPES.find((type) => isSupported(type)) ?? null;
}

/** The extension a finished film should carry, read off its own type. */
export function extensionFor(mimeType: string): string {
  return mimeType.startsWith("video/mp4") ? "mp4" : "webm";
}

export type RenderRequest = {
  /** Frame URLs, in the order they should run — oldest first. */
  urls: readonly string[];
  frame: Frame;
  /** Frames per second, from `frameRate`. */
  fps: number;
  /**
   * Whether the "Selfeed" mark goes along the bottom of every frame. The
   * phone's render takes this as a parameter for the same reason: it is
   * the render's contract — what the app is entitled to produce — and not
   * a preference read off a settings struct.
   */
  watermarked: boolean;
  /** 0…1 as frames are drawn. */
  onProgress?: (fraction: number) => void;
  /** Aborts between frames; a half-written film is thrown away. */
  signal?: AbortSignal;
};

export type RenderResult = { blob: Blob; mimeType: string; drawn: number; skipped: number };

export class RenderUnsupported extends Error {}
export class RenderEmpty extends Error {}
/** The recorder itself gave up — a codec fault, or the stream ending. */
export class RenderFailed extends Error {}

/**
 * Draws every frame onto one canvas and records the canvas as it goes.
 *
 * A frame that will not load is skipped rather than fatal, and counted: a
 * signed URL can expire mid-render and a film missing one day out of four
 * hundred is still the film somebody asked for. A run where *nothing* loaded
 * is a different thing and throws, because a video of the empty canvas is
 * not a film — it is a bug that plays.
 */
export async function renderFilm({
  urls,
  frame,
  fps,
  watermarked,
  onProgress,
  signal,
}: RenderRequest): Promise<RenderResult> {
  const mimeType = supportedMimeType();
  if (!mimeType) throw new RenderUnsupported("this browser cannot record video");
  if (urls.length === 0) throw new RenderEmpty("nothing to render");

  const size = canvasSize(frame);
  const canvas = document.createElement("canvas");
  canvas.width = size.width;
  canvas.height = size.height;
  const context = canvas.getContext("2d");
  if (!context) throw new RenderUnsupported("this browser has no 2d canvas");

  // The dark scheme's background, always — the same choice iOS makes. A film
  // exported on a light afternoon and watched in a dark room should not have
  // a bright border baked into it.
  context.fillStyle = "#0E0B0A";

  const stream = canvas.captureStream(fps);
  const recorder = new MediaRecorder(stream, { mimeType });
  const chunks: Blob[] = [];
  recorder.ondataavailable = (event) => {
    if (event.data.size > 0) chunks.push(event.data);
  };
  /**
   * Settles when the recorder has written its last chunk — and, just as
   * importantly, when it never will.
   *
   * `onstop` alone is not enough to wait on. A recorder that faults mid-run
   * fires `onerror` and never `onstop`, so awaiting only the happy path
   * leaves this hanging for good: the screen sits at "Recording… 100%" and
   * Cancel cannot help, because by then the loop it watches has finished.
   * An unbounded await on a browser callback is a hang waiting for a codec
   * to misbehave.
   */
  let fault: RenderFailed | null = null;
  const finished = new Promise<void>((resolve, reject) => {
    recorder.onstop = () => resolve();
    recorder.onerror = () => {
      fault = new RenderFailed("the recorder stopped early");
      reject(fault);
    };
  });
  // Attached now, not at the await below. The fault can land while the loop
  // is still drawing, and a rejection nobody is listening to yet is an
  // `unhandledrejection` — which this app reports to Sentry as a crash it
  // did not have. Awaiting the same promise later still throws.
  finished.catch(() => {});

  const placement = framePlacement(frame);
  const interval = 1000 / fps;
  const ahead = new FramesAhead(urls);
  let drawn = 0;
  let skipped = 0;

  try {
    // The first frame in hand before the recorder starts: a film used to
    // open on the blank canvas, held for as long as its first frame took
    // to arrive. Through `whileFetching` for its race with Cancel alone —
    // there is no recording yet to hold.
    await whileFetching(recorder, ahead.take(0).promise, signal);
    if (signal?.aborted) throw new DOMException("render aborted", "AbortError");
    // Inside the try: `start` can refuse a type `isTypeSupported` accepted,
    // and outside it the capture stream would stay live with nothing left
    // holding it — a camera-less leak, but a leak.
    recorder.start();
    for (let position = 0; position < urls.length; position += 1) {
      // Checked every frame alongside the abort: there is nothing to be
      // gained by drawing four hundred more frames into a recorder that has
      // already given up.
      if (fault) throw fault;
      if (signal?.aborted) throw new DOMException("render aborted", "AbortError");
      await whileAway(recorder, signal);
      if (signal?.aborted) throw new DOMException("render aborted", "AbortError");
      // A frame that is not here yet holds the recorder, not the film — see
      // `FramesAhead`.
      const slot = ahead.take(position);
      const image = slot.settled
        ? await slot.promise
        : await whileFetching(recorder, slot.promise, signal);
      if (signal?.aborted) throw new DOMException("render aborted", "AbortError");
      if (image) {
        context.fillRect(0, 0, size.width, size.height);
        context.drawImage(image, placement.x, placement.y, placement.width, placement.height);
        if (watermarked) drawWatermark(context, size.width, size.height);
        drawn += 1;
      } else {
        skipped += 1;
      }
      onProgress?.((position + 1) / urls.length);
      // The recorder samples the canvas in real time, so a frame has to stay
      // on it for its own share of a second — and only that: the next few
      // frames are already on their way while this one is on the canvas.
      await wait(interval);
    }
  } finally {
    if (recorder.state !== "inactive") recorder.stop();
    for (const track of stream.getTracks()) track.stop();
  }
  await finished;

  if (drawn === 0) throw new RenderEmpty("every frame failed to load");
  return { blob: new Blob(chunks, { type: mimeType }), mimeType, drawn, skipped };
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Holds the recording while the tab is away, and only then.
 *
 * The recorder samples the canvas in real time, and a hidden tab is not
 * real time: the browser slows its timers to one a second, and stops
 * painting the canvas the stream is drawn from. A render left for another
 * tab came back as a film with a frozen stretch in the middle, every frame
 * after it held for a second, and a length nothing on the screen had
 * promised. The phone's answer is to stop the export when the app goes to
 * the background and throw the unfinished video away; a browser can do
 * better, because a paused recorder keeps no time — the film picks up
 * where it left off, its length what the screen said.
 *
 * Nothing here for a browser whose document has no visibility, and nothing
 * for a recorder that cannot pause: both just keep recording, as before.
 */
async function whileAway(recorder: MediaRecorder, signal?: AbortSignal): Promise<void> {
  if (typeof document === "undefined" || !document.hidden) return;
  if (recorder.state === "recording") recorder.pause();
  await new Promise<void>((resolve) => {
    const settle = () => {
      document.removeEventListener("visibilitychange", onChange);
      signal?.removeEventListener("abort", settle);
      resolve();
    };
    const onChange = () => {
      if (!document.hidden) settle();
    };
    document.addEventListener("visibilitychange", onChange);
    signal?.addEventListener("abort", settle);
  });
  if (recorder.state === "paused") recorder.resume();
}

/** How many frames are on their way while one is on the canvas. */
const LOOKAHEAD = 6;

type Slot = { promise: Promise<HTMLImageElement | null>; settled: boolean };

/**
 * The frames, fetched ahead of the recorder.
 *
 * Each frame used to be fetched when its turn came, with the recorder
 * running: the fetch was baked into the film as so much more of the frame
 * before it. On a fast line the frames come in a few milliseconds and the
 * film was what the screen promised; on a phone's, at a hundred and fifty
 * milliseconds a frame, forty days promised as five seconds came out as
 * eleven, and at four hundred as twenty-one — measured with ffprobe on the
 * file. The next few frames are fetched while the current one is on the
 * canvas, and a frame that has still not come when its turn arrives holds
 * the recorder — which keeps no time while paused — rather than the film.
 * A few frames ahead, not all of them: four hundred decoded photographs
 * are more memory than a phone's tab is given.
 */
class FramesAhead {
  private readonly slots = new Map<number, Slot>();

  constructor(private readonly urls: readonly string[]) {}

  /** The frame at `position`, its fetch begun along with the few after it. */
  take(position: number): Slot {
    const end = Math.min(position + LOOKAHEAD, this.urls.length);
    for (let index = position; index < end; index += 1) this.begin(index);
    // Positions are taken in order; the one before is finished with.
    this.slots.delete(position - 1);
    return this.slots.get(position) ?? { promise: Promise.resolve(null), settled: true };
  }

  private begin(index: number): void {
    if (this.slots.has(index)) return;
    const slot: Slot = { promise: Promise.resolve(null), settled: false };
    slot.promise = loadImage(this.urls[index] ?? "").then((image) => {
      slot.settled = true;
      return image;
    });
    this.slots.set(index, slot);
  }
}

/**
 * Holds the recording until a late frame arrives — or Cancel says to stop
 * waiting for it — so the wait is not in the film.
 */
async function whileFetching(
  recorder: MediaRecorder,
  pending: Promise<HTMLImageElement | null>,
  signal?: AbortSignal,
): Promise<HTMLImageElement | null> {
  if (recorder.state === "recording") recorder.pause();
  try {
    return await new Promise<HTMLImageElement | null>((resolve) => {
      const giveUp = () => resolve(null);
      signal?.addEventListener("abort", giveUp, { once: true });
      pending.then(resolve, giveUp).finally(() => signal?.removeEventListener("abort", giveUp));
    });
  } finally {
    if (recorder.state === "paused") recorder.resume();
  }
}

/**
 * One frame, or null if it will not come.
 *
 * `crossOrigin` is set because the canvas is read back at the end: an image
 * drawn from another origin without CORS taints the canvas and every read
 * after that throws, which would fail the whole render on the last step
 * rather than on the frame that caused it.
 */
function loadImage(url: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = url;
  });
}
